// src/components/SimpleComponents/SimpleMultiSelect.jsx
// *******************************************************************************
// SIMPLE MULTI SELECT
// *******************************************************************************
// Async multi-select component for server-side search with multiple selection.
// Accepts an `onFetch` callback that receives the search value and must return
// an array of options: [{ value, label }].
// Accepts an optional `onFetchByValue` callback to resolve labels for existing
// form values on mount (e.g., after page refresh).
// When minSearchLength=0, loads all options on mount automatically.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Select, Spin, Flex, Typography, Empty } from 'antd';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEBOUNCE_DELAY = 400;
const MIN_SEARCH_LENGTH = 2;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleMultiSelect = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  placeholder = 'select',
  onChange,
  onBlur,
  validateTrigger = 'onBlur',
  showFeedback = false,
  required: requiredProp,
  allowClear = true,
  maxTagCount = 'responsive',

  // Async-specific props
  onFetch, // (searchValue: string) => Promise<[{ value, label }]>
  onFetchByValue, // (id: string) => Promise<{ value, label } | null>
  minSearchLength = MIN_SEARCH_LENGTH,
  debounceDelay = DEBOUNCE_DELAY,
  emptyText = 'no_data',
  searchHintText = 'type_to_search',

  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields, shouldShowErrors, requiredFields, form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const debounceTimerRef = useRef(null);
  const lastSearchRef = useRef('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const required = requiredProp ?? requiredFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [customValidationStatus, setCustomValidationStatus] = useState('');
  const [customValidationMessage, setCustomValidationMessage] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Auto-load all options on mount when minSearchLength is 0
  useEffect(() => {
    if (minSearchLength !== 0 || !onFetch) return;

    setLoading(true);
    onFetch('')
      .then(results => {
        setOptions(Array.isArray(results) ? results : []);
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve labels for existing form values on mount (e.g., after page refresh)
  useEffect(() => {
    if (!onFetchByValue || !form) return;

    const fieldName = Array.isArray(name) ? name : [name];
    const currentValues = form.getFieldValue(fieldName);
    if (!Array.isArray(currentValues) || currentValues.length === 0) return;

    // Only resolve values that are not already in options
    const unresolvedIds = currentValues.filter(
      id => !options.find(opt => opt.value === id)
    );
    if (unresolvedIds.length === 0) return;

    let active = true;

    const resolveLabels = async () => {
      const resolved = await Promise.all(
        unresolvedIds.map(id => onFetchByValue(id).catch(() => null))
      );
      if (!active) return;

      const validOptions = resolved.filter(Boolean);
      if (validOptions.length > 0) {
        setOptions(prev => {
          const merged = [...prev];
          for (const opt of validOptions) {
            if (!merged.find(m => m.value === opt.value)) {
              merged.push(opt);
            }
          }
          return merged;
        });
      }
    };

    resolveLabels();

    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Activate validation when form shows errors
  useEffect(() => {
    if (shouldShowErrors) setShouldValidate(true);
  }, [shouldShowErrors]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(
    searchValue => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (searchValue.length < minSearchLength) {
        // If minSearchLength=0, keep existing options visible when search is cleared
        if (minSearchLength === 0) return;
        setOptions([]);
        setLoading(false);
        return;
      }

      const normalized = searchValue.toLowerCase().trim();
      if (normalized === lastSearchRef.current) return;

      setLoading(true);

      debounceTimerRef.current = setTimeout(async () => {
        try {
          lastSearchRef.current = normalized;
          const results = await onFetch(searchValue);
          setOptions(Array.isArray(results) ? results : []);
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceDelay);
    },
    [minSearchLength, debounceDelay, onFetch]
  );

  // ANTD v6 fix: controlled open state ensures dropdown closes correctly
  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
    if (!visible) lastSearchRef.current = '';
  }, []);

  const handleChange = useCallback(
    (value, option) => {
      setShouldValidate(true);
      if (typeof onChange === 'function') onChange(value, option);
    },
    [onChange]
  );

  const handleBlur = useCallback(
    ev => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (typeof onBlur === 'function') setTimeout(() => onBlur(ev), 0);
    },
    [onBlur]
  );

  const handleDeselect = useCallback(() => {
    setShouldValidate(true);
  }, []);

  const handleClear = useCallback(() => {
    lastSearchRef.current = '';
    setShouldValidate(true);
    if (typeof onChange === 'function') onChange([], []);
  }, [onChange]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <Flex justify="center" style={{ padding: 16 }}>
          <Spin size="small" />
        </Flex>
      );
    }

    if (lastSearchRef.current && options.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translateX(emptyText)}
          style={{ margin: '16px 0' }}
        />
      );
    }

    if (minSearchLength > 0) {
      return (
        <Flex justify="center" style={{ padding: 16 }}>
          <Text type="secondary">{translateX(searchHintText)}</Text>
        </Flex>
      );
    }

    return null;
  }, [loading, options.length, emptyText, searchHintText, minSearchLength]);

  // Custom validation after user interacts
  const validationStatus = useMemo(() => {
    if (!shouldValidate) return { status: '', message: '' };

    const isEmpty = !customValidationStatus && required && !options.length;
    if (isEmpty) return { status: 'error', message: translateX('required_field') };

    return { status: customValidationStatus, message: customValidationMessage };
  }, [shouldValidate, required, options.length, customValidationStatus, customValidationMessage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
      required={required}
      customValidationStatus={validationStatus.status}
      customValidationMessage={validationStatus.message}
    >
      <Select
        ref={elementRef}
        id={flattenFieldName}
        mode="multiple"
        style={{ minHeight: 40, ...style }}
        showSearch
        allowClear={allowClear}
        disabled={isDisabled}
        placeholder={translateX(placeholder)}
        filterOption={false}
        maxTagCount={maxTagCount}
        onSearch={handleSearch}
        onChange={handleChange}
        onDeselect={handleDeselect}
        onClear={handleClear}
        onBlur={handleBlur}
        open={open}
        onOpenChange={handleDropdownVisibleChange}
        notFoundContent={notFoundContent}
        loading={loading}
        options={options}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleMultiSelect;
